/***********************************************
 * Represents the concept of a design revision.
 * A design revision results from small edits to
 * a parent design or other revision. Currently,
 * this can be from a single live swap in Studio
 * or Bessie edits.
 ************************************************/
import { camelizeKeys } from 'humps';
import Render from './RenderV2';

class Revision {
  static SOURCE_STUDIO= 'studio';
  static SOURCE_BESSIE = 'bessie';
  static SOURCE_SHOWROOM = 'showroom';

  static defaultProps = {
    uuid: null, // uuid from API
    parentDesignId: null, // id of parent design this revision belongs to
    parentSpaceId: null, // id of parent space this revision belongs to
    status: null, // one of: created | preparing | ready | failed
    source: Revision.SOURCE_STUDIO,

    // Mapping of render camera names to render UIDs.
    renders: {},

    // Replacement info.
    // These are the products that were updated/swapped in this revision.
    // Currently, this is limited to one replacement per studio live swap.
    // This information is not applicable to Bessie revisions.
    // Format is mapping of hotspot uuid to replacement product id.
    replacements: {}
  };

  // Converts warehouse response to our internal model.
  static createRevisionFromWarehouseDesignSnapshot ({ whDesignSnapshot }) {
    const snapshot = camelizeKeys(whDesignSnapshot);
    const uuid = snapshot.id;

    // Important: we do not want to camelize the camera name.
    let renders = {};

    Object.keys(whDesignSnapshot.renderings).forEach(cameraName => {
      const renderUid = Render.createRenderUid({ revisionUuid: uuid, cameraName });
      renders[cameraName] = renderUid;
    });

    // Important: we do not want to camelize the hotspot uuid.
    const replaceInfo = whDesignSnapshot.replace_info || {};
    let replacements = {};
    Object.keys(replaceInfo).forEach(hotspotUuid => {
      replacements[hotspotUuid] = {productId: replaceInfo[hotspotUuid].product_id, newInstanceId: replaceInfo[hotspotUuid].new_instance_id};
    });

    return new Revision({
      uuid,
      parentDesignId: snapshot.designPk,
      parentSpaceId: snapshot.userData.spaceId,
      renders,
      replacements,
      source: snapshot.source === Revision.SOURCE_BESSIE
        ? Revision.SOURCE_BESSIE
        : (
          snapshot.source === Revision.SOURCE_SHOWROOM
            ? Revision.SOURCE_SHOWROOM
            : Revision.SOURCE_STUDIO
        ),
      status: snapshot.status
    });
  }

  /** Creates a new design instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...Revision.defaultProps, ...overrides };
  }

  /** Creates a new designsnapshot copied from this with optional overrides. */
  makeCopy (overrides) {
    return new Revision({ ...this.fields, ...overrides });
  }

  // =======
  // GETTERS
  // =======
  /** Returns the uuid for this revision. */
  uuid () { return this.fields.uuid; }

  /** Returns the parent design ID for this revision. */
  parentDesignId () { return this.fields.parentDesignId; }

  /** Returns the parent space ID for this revision. */
  parentSpaceId () { return this.fields.parentSpaceId; }

  /** Revision status checks. */
  isReady () { return this.fields.status === 'ready'; }
  isFailed () { return this.fields.status === 'failed'; }

  /** Revision source checks. */
  isFromStudio () { return this.fields.source === Revision.SOURCE_STUDIO; }
  isFromShowroom () { return this.fields.source === Revision.SOURCE_SHOWROOM; }
  isFromBessie () { return this.fields.source === Revision.SOURCE_BESSIE; }

  /** Returns true if the revision has at least one render. */
  hasRenders () { return Object.keys(this.fields.renders).length > 0; }

  /**
   * Returns the render UID for provided camera name.
   * Otherwise, returns null.
   */
  renderFor (cameraName) {
    return this.fields.renders[cameraName] || null;
  }

  /**
   * Returns object of all of the renders.
   * key: camera name, value: render uid
   * if no renders, returns empty object.
   */
  renders () {
    return this.fields.renders;
  }

  /** Returns the replacement hotspot uuids for this revision. */
  replacementHotspotUuids () {
    return Object.keys(this.fields.replacements);
  }

  /** Returns the replacement productIds for this revision. */
  replacementProductIds () {
    return this.replacementHotspotUuids().map(hotspotUuid => {
      return this.fields.replacements[hotspotUuid].productId;
    });
  }

  /** Returns the replacement objects. { productId, newInstanceId } */
  replacements () {
    return this.fields.replacements;
  }
}

export default Revision;
